// pages/ShippingPage.tsx - Updated shipping with distance calculator and translations
import React, { useState } from 'react';
import { Truck, Package, MapPin, CheckCircle, Phone, Calculator } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ShippingPage: React.FC = () => {
  const { t } = useLanguage();
  const [showCalculator, setShowCalculator] = useState(false);
  const [fromAddress, setFromAddress] = useState('');
  const [distance, setDistance] = useState<number | null>(null);
  const [tripType, setTripType] = useState<'pickup' | 'delivery' | 'both'>('pickup');

  const calculatePrice = () => {
    // In real implementation, this would use Google Maps Distance Matrix API
    // For now, using a placeholder calculation
    const estimatedKm = 50; // Placeholder - would be calculated via API
    setDistance(estimatedKm);
  };

  const getTotalKm = () => {
    if (!distance) return 0;
    const multiplier = tripType === 'both' ? 4 : 2;
    return distance * multiplier;
  };

  const getTotalPrice = () => {
    return (getTotalKm() * 1.21).toFixed(2);
  };

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-cyan-900 via-blue-900 to-teal-900 py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t('shipping.title')}
          </h1>
          <p className="text-xl text-cyan-100 max-w-3xl">
            {t('shipping.subtitle')}
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 via-gray-50/50 to-transparent pointer-events-none" />
      </div>

      <div className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t('shipping.options')}</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <MapPin className="w-12 h-12 text-cyan-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">{t('shipping.bringYourself')}</h3>
              <p className="text-gray-600 mb-4">
                {t('shipping.bringYourselfDesc')}
              </p>
              <p className="text-2xl font-bold text-cyan-600">{t('shipping.free')}</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>Turuntie 3, 37830 Akaa</p>
                <p>{t('shipping.byAppointment')}</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-blue-500">
              <Package className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">{t('shipping.postIt')}</h3>
              <p className="text-gray-600 mb-4">
                {t('shipping.postItDesc')}
              </p>
              <p className="text-2xl font-bold text-blue-600">{t('shipping.ownCost')}</p>
              <p className="text-sm text-gray-500 mt-2">{t('shipping.usually')}</p>
              <div className="mt-4">
                <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                  {t('shipping.mostPopular')}
                </span>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <Truck className="w-12 h-12 text-cyan-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">{t('shipping.pickupService')}</h3>
              <p className="text-gray-600 mb-4">
                {t('shipping.pickupServiceDesc')}
              </p>
              <p className="text-2xl font-bold text-cyan-600">1.21 €/km</p>
              <p className="text-sm text-gray-500 mt-2">{t('shipping.dependsOnDistance')}</p>
              <div className="mt-4">
                <button
                  onClick={() => setShowCalculator(!showCalculator)}
                  className="inline-flex items-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-full font-semibold hover:bg-cyan-700 transition-colors text-sm"
                >
                  <Calculator className="w-4 h-4" />
                  {t('shipping.calculatePrice')}
                </button>
              </div>
            </div>
          </div>

          {/* Distance Calculator */}
          {showCalculator && (
            <div className="bg-white p-8 rounded-2xl shadow-lg mb-12 border-2 border-cyan-500">
              <h3 className="text-2xl font-bold mb-6">{t('shipping.priceCalculator')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('shipping.fromAddress')}
                  </label>
                  <input
                    type="text"
                    value={fromAddress}
                    onChange={(e) => setFromAddress(e.target.value)}
                    placeholder={t('shipping.addressPlaceholder')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('shipping.serviceType')}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        checked={tripType === 'pickup'}
                        onChange={() => setTripType('pickup')}
                        className="w-4 h-4 text-cyan-600"
                      />
                      <span>{t('shipping.pickupOnly')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        checked={tripType === 'delivery'}
                        onChange={() => setTripType('delivery')}
                        className="w-4 h-4 text-cyan-600"
                      />
                      <span>{t('shipping.deliveryOnly')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        checked={tripType === 'both'}
                        onChange={() => setTripType('both')}
                        className="w-4 h-4 text-cyan-600"
                      />
                      <span>{t('shipping.pickupAndDelivery')}</span>
                    </label>
                  </div>
                </div>

                <button
                  onClick={calculatePrice}
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all"
                >
                  {t('shipping.calculatePrice')}
                </button>

                {distance !== null && (
                  <div className="bg-cyan-50 p-6 rounded-lg border border-cyan-200">
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">{t('shipping.oneWayDistance')}: <strong>{distance} km</strong></p>
                      <p className="text-sm text-gray-600">{t('shipping.pricePerKm')}: <strong>1.21 €/km</strong></p>
                      <div className="pt-3 mt-3 border-t border-cyan-200">
                        <p className="text-2xl font-bold text-cyan-600">
                          {t('shipping.estimatedPrice')}: {getTotalPrice()} €
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-4">
                      {t('shipping.priceNote')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-white p-8 rounded-2xl shadow-lg mb-12">
            <h3 className="text-2xl font-bold mb-6">{t('shipping.packagingInstructions')}</h3>
            <ul className="space-y-3">
              {[
                t('shipping.packStep1'),
                t('shipping.packStep2'),
                t('shipping.packStep3'),
                t('shipping.packStep4')
              ].map((step, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-2xl">
              <h3 className="text-xl font-bold mb-4">{t('shipping.pickupArea')}</h3>
              <p className="text-gray-700">
                {t('shipping.pickupAreaDesc')}
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-2xl">
              <h3 className="text-xl font-bold mb-4">{t('shipping.deliveryTime')}</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-cyan-600 rounded-full" />
                  <span>{t('shipping.pickupTime')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-cyan-600 rounded-full" />
                  <span>{t('shipping.postTime')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            {t('shipping.needHelp')}
          </h2>
          <p className="text-xl text-cyan-100 mb-8">
            {t('shipping.needHelpDesc')}
          </p>
          <a
            href="tel:+358400416466"
            className="inline-flex items-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all"
          >
            <Phone className="w-5 h-5" />
            +358 400 416 466
          </a>
        </div>
      </div>
    </div>
  );
};

export default ShippingPage;
